import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Offcanvas } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  IconCalendarEvent,
  IconCalendarPlus,
  IconEdit,
  IconPackage,
  IconRefresh,
  IconSearch,
  IconTrash,
  IconUsers,
} from "@tabler/icons-react";
import { useUser } from "contexts/v1/masterData/UserContext";
import { API_URL, URLS } from "utils/constants";
import { getRequest, postRequest } from "services/api";
import "../public-holiday-quota-page.css";

const currentYear = new Date().getFullYear();

const getData = (response) => response?.data?.data || response?.data || [];

const toDateKey = (value) => {
  if (!value) return "";
  return String(value).slice(0, 10);
};

const formatDate = (value) => {
  const dateKey = toDateKey(value);
  if (!dateKey) return "-";
  return new Date(dateKey).toLocaleDateString("id-ID", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getDepartmentName = (user) =>
  user?.department?.name ||
  user?.department_name ||
  user?.position?.department?.name ||
  "Tanpa Departemen";

const requestWithBody = async (url, method, payload) => {
  const token = localStorage.getItem("token");
  const options = {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };

  if (payload) options.body = JSON.stringify(payload);

  const response = await fetch(`${API_URL}${url}`, options);
  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const error = new Error(data.message || "Request Public Holiday gagal.");
    error.code = data.code;
    error.errors = data.errors;
    throw error;
  }

  return data;
};

const createDemoPackages = (year, availableUsers = []) => {
  const fallbackUsers = [
    { id: "demo-1", name: "NI WAYAN YANTI", department: { name: "BARISTA" }, email: "yanti@example.test" },
    { id: "demo-2", name: "I WAYAN SUBRATA", department: { name: "SECURITY" }, email: "subrata@example.test" },
    { id: "demo-3", name: "KARTINI", department: { name: "HOUSEKEEPING" }, email: "kartini@example.test" },
    { id: "demo-4", name: "PUTU ANTARA", department: { name: "GUIDE" }, email: "antara@example.test" },
  ];
  const users = availableUsers.slice(0, 8).length ? availableUsers.slice(0, 8) : fallbackUsers;
  const packages = [
    {
      id: "demo-package-1",
      name: `Cuti Bersama Galungan ${year}`,
      start_date: `${year}-04-01`,
      end_date: `${year}-04-10`,
      quota: 2,
      description: "Hak PH khusus staff operasional saat Galungan.",
    },
    {
      id: "demo-package-2",
      name: `Cuti Bersama Lebaran ${year}`,
      start_date: `${year}-08-01`,
      end_date: `${year}-08-12`,
      quota: 3,
      description: "Hak PH untuk staff yang mendapat pengganti libur Lebaran.",
    },
  ];
  const assignments = {
    "demo-package-1": users.slice(0, 4).map((user, index) => ({
      id: `demo-assignment-1-${index}`,
      holiday_package_id: "demo-package-1",
      user_id: user.id,
      user,
      used_count: index % 2,
      remaining_quota: Math.max(2 - (index % 2), 0),
    })),
    "demo-package-2": users.slice(2, 7).map((user, index) => ({
      id: `demo-assignment-2-${index}`,
      holiday_package_id: "demo-package-2",
      user_id: user.id,
      user,
      used_count: index % 3,
      remaining_quota: Math.max(3 - (index % 3), 0),
    })),
  };

  return { packages, assignments };
};

const PHHubQuotaTab = ({ setActiveTab }) => {
  const navigate = useNavigate();
  const { users } = useUser();
  const [packages, setPackages] = useState([]);
  const [packageFilterId, setPackageFilterId] = useState("");
  const [assignmentCache, setAssignmentCache] = useState({});
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [isDemoData, setIsDemoData] = useState(false);

  const stats = useMemo(() => {
    const quota = packages.reduce((total, item) => total + Number(item.quota || 0), 0);
    return { packages: packages.length, quota };
  }, [packages]);

  const visiblePackages = useMemo(
    () => packageFilterId ? packages.filter((item) => item.id === packageFilterId) : packages,
    [packageFilterId, packages]
  );

  const loadDemoData = useCallback(() => {
    const demo = createDemoPackages(currentYear, users.data || []);
    setPackages(demo.packages);
    setAssignmentCache(demo.assignments);
    setIsDemoData(true);
  }, [users.data]);

  const loadPackages = useCallback(async () => {
    const response = await getRequest("/ahr/holiday-packages?all=1");
    setPackages(getData(response));
    setAssignmentCache({});
    setIsDemoData(false);
  }, []);

  const loadAssignments = useCallback(async (packageId) => {
    if (!packageId) return [];
    if (isDemoData) return assignmentCache[packageId] || [];

    const response = await getRequest(`/ahr/holiday-package-assignments?filter[holiday_package_id]=${packageId}&include=user,package&all=1`);
    const items = getData(response);
    setAssignmentCache((current) => ({ ...current, [packageId]: items }));
    return items;
  }, [assignmentCache, isDemoData]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      await loadPackages();
    } catch (requestError) {
      loadDemoData();
      setError("Endpoint Holiday Package belum terbaca dari frontend. Data contoh sedang ditampilkan agar desain tetap bisa diuji.");
    } finally {
      setLoading(false);
    }
  }, [loadDemoData, loadPackages]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openAssignModal = async (holidayPackage) => {
    try {
      const assignments = await loadAssignments(holidayPackage.id);
      setModal({ type: "assign", package: holidayPackage, assignments });
    } catch (requestError) {
      setError(requestError.message || "Gagal memuat assignment staff PH.");
    }
  };

  const openUseModal = async (holidayPackage) => {
    // Instead of navigating, use setActiveTab if passed, or just navigate to the old URL if needed.
    // For Hub, we will use setActiveTab to switch to 'use' tab and pass package_id in URL.
    navigate(`?package_id=${holidayPackage.id}`);
    if (setActiveTab) {
      setActiveTab("use");
    } else {
      navigate(`${URLS.v1.HR_MANAGE_PUBLIC_HOLIDAY_USE}?package_id=${holidayPackage.id}`);
    }
  };

  const submitPackage = async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const editingPackage = modal?.mode === "edit" ? modal.package : null;
    const payload = {
      name: form.get("name"),
      start_date: toDateKey(form.get("start_date")),
      end_date: toDateKey(form.get("end_date")),
      quota: Number(form.get("quota")),
      description: form.get("description") || null,
    };

    setSaving(true);
    try {
      if (isDemoData) {
        if (editingPackage) {
          setPackages((items) => items.map((item) => (item.id === editingPackage.id ? { ...item, ...payload } : item)));
        } else {
          const newPackage = { ...payload, id: `demo-package-${Date.now()}` };
          setPackages((items) => [...items, newPackage]);
          setPackageFilterId(newPackage.id);
        }
      } else if (editingPackage) {
        await requestWithBody(`/ahr/holiday-packages/${editingPackage.id}`, "PUT", payload);
        await loadPackages();
      } else {
        const response = await postRequest("/ahr/holiday-packages", payload);
        const packageId = response?.data?.id || response?.data?.data?.id;
        await loadPackages();
        if (packageId) setPackageFilterId(packageId);
      }
      setModal(null);
    } catch (requestError) {
      setError(requestError.message || `Gagal ${editingPackage ? "mengubah" : "membuat"} Holiday Package.`);
    } finally {
      setSaving(false);
    }
  };

  const deletePackage = async (holidayPackage) => {
    const isConfirmed = window.confirm(`Hapus package "${holidayPackage.name}"?`);
    if (!isConfirmed) return;

    setSaving(true);
    try {
      if (isDemoData) {
        setPackages((items) => items.filter((item) => item.id !== holidayPackage.id));
        setPackageFilterId((current) => current === holidayPackage.id ? "" : current);
        setAssignmentCache((current) => {
          const next = { ...current };
          delete next[holidayPackage.id];
          return next;
        });
      } else {
        await requestWithBody(`/ahr/holiday-packages/${holidayPackage.id}`, "DELETE");
        await loadPackages();
        setPackageFilterId((current) => current === holidayPackage.id ? "" : current);
      }
    } catch (requestError) {
      setError(requestError.message || "Gagal menghapus Holiday Package.");
    } finally {
      setSaving(false);
    }
  };

  const submitAssignUsers = async (event, selectedIds) => {
    event.preventDefault();
    const holidayPackage = modal?.package;
    if (!holidayPackage) return;

    const currentIds = new Set((modal.assignments || []).map((assignment) => assignment.user_id || assignment.user?.id).filter(Boolean));
    const nextIds = new Set(selectedIds);
    const userIdsToAssign = selectedIds.filter((id) => !currentIds.has(id));
    const userIdsToUnassign = [...currentIds].filter((id) => !nextIds.has(id));

    setSaving(true);
    try {
      if (isDemoData) {
        const selectedUsers = (users.data || []).filter((user) => selectedIds.includes(user.id));
        setAssignmentCache((current) => ({
          ...current,
          [holidayPackage.id]: selectedUsers.map((user) => ({
            id: `demo-assignment-${holidayPackage.id}-${user.id}`,
            holiday_package_id: holidayPackage.id,
            user_id: user.id,
            user,
            used_count: 0,
            remaining_quota: Number(holidayPackage.quota || 0),
          })),
        }));
      } else {
        if (userIdsToAssign.length) {
          await postRequest(`/ahr/holiday-packages/${holidayPackage.id}/assign-users`, { user_ids: userIdsToAssign });
        }
        if (userIdsToUnassign.length) {
          await requestWithBody(`/ahr/holiday-packages/${holidayPackage.id}/assign-users`, "DELETE", { user_ids: userIdsToUnassign });
        }
        await loadAssignments(holidayPackage.id);
      }
      setModal(null);
    } catch (requestError) {
      setError(requestError.message || "Gagal menyimpan assignment staff PH.");
    } finally {
      setSaving(false);
    }
  };

  const submitUseHoliday = async (event, selectedIds, dates) => {
    event.preventDefault();
    const holidayPackage = modal?.package;
    if (!holidayPackage || !selectedIds.length || !dates.length) return;

    setSaving(true);
    try {
      if (!isDemoData) {
        await postRequest(`/ahr/holiday-packages/${holidayPackage.id}/schedule-batch`, {
          status: "draft",
          user_ids: selectedIds,
          dates,
        });
        await loadAssignments(holidayPackage.id);
      }
      setModal(null);
    } catch (requestError) {
      setError(requestError.message || "Gagal memakai PH lewat schedule batch.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="p-3 public-holiday-quota-page">
        <div className="d-flex justify-content-end mb-4 g-3">
            <div className="btn-list">
              <button className="btn btn-light border" onClick={loadData} disabled={loading}>
                <IconRefresh size={17} className="me-1" /> Refresh
              </button>
              <button className="btn btn-light border" onClick={loadDemoData}>Sample Data</button>
              <button className="btn btn-primary" onClick={() => setModal({ type: "package", mode: "create" })}>
                <IconPackage size={17} className="me-1" /> Buat Package
              </button>
            </div>
          </div>
        <div className="card border-0 shadow-sm mb-4 ph-toolbar">
          <div className="card-body py-3">
            <div className="row g-3 align-items-center">
              <div className="col-md-5 col-lg-4">
                <label className="form-label text-muted small fw-bold text-uppercase mb-1">Pilih Package</label>
                <select className="form-select" value={packageFilterId} onChange={(event) => setPackageFilterId(event.target.value)}>
                  <option value="">Semua Package</option>
                  {packages.map((holidayPackage) => (
                    <option key={holidayPackage.id} value={holidayPackage.id}>{holidayPackage.name}</option>
                  ))}
                </select>
              </div>
              <div className="col">
                <div className="text-muted small">Package sudah punya periode masing-masing, jadi tidak perlu filter tahun/next/prev di halaman ini.</div>
              </div>
              <div className="col-md-auto text-md-end">
                <div className="ph-summary-chip">{stats.packages} package | total quota {stats.quota} hari</div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-warning d-flex align-items-center justify-content-between mb-4">
            <span>{error}</span>
            <button className="btn btn-sm btn-warning" onClick={() => setError("")}>Tutup</button>
          </div>
        )}
        {isDemoData && (
          <div className="ph-demo-banner mb-4">
            <span>DATA CONTOH</span> Data di bawah ini hanya untuk preview desain saat endpoint belum terbaca.
          </div>
        )}

        <div className="card border-0 shadow-sm overflow-hidden">
          <div className="card-header d-flex align-items-center justify-content-between">
            <div>
              <h3 className="card-title mb-1">Daftar Holiday Package</h3>
              <div className="text-muted small">CRUD quota ada di tabel ini. Assign dan penggunaan PH dibuka dari action tiap package.</div>
            </div>
          </div>
          <div className="table-responsive">
            <table className="table table-vcenter card-table ph-package-table mb-0">
              <thead>
                <tr>
                  <th>Nama Package</th>
                  <th>Periode</th>
                  <th className="text-center">Quota / Staff</th>
                  <th>Deskripsi</th>
                  <th className="text-end">Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="5" className="text-center py-5">
                      <span className="spinner-border spinner-border-sm text-primary me-2" />Memuat Holiday Package...
                    </td>
                  </tr>
                ) : visiblePackages.length ? visiblePackages.map((holidayPackage) => (
                  <tr key={holidayPackage.id}>
                    <td>
                      <div className="fw-bold">{holidayPackage.name}</div>
                      <div className="text-muted small">{holidayPackage.id}</div>
                    </td>
                    <td>{formatDate(holidayPackage.start_date)} - {formatDate(holidayPackage.end_date)}</td>
                    <td className="text-center">
                      <span className="ph-number-chip ph-quota-value">{holidayPackage.quota || 0}</span>
                    </td>
                    <td className="text-muted">{holidayPackage.description || "-"}</td>
                    <td className="text-end">
                      <div className="btn-list justify-content-end">
                        <button className="btn btn-sm btn-light border" onClick={() => setModal({ type: "package", mode: "edit", package: holidayPackage })}>
                          <IconEdit size={15} className="me-1" /> Edit
                        </button>
                        <button className="btn btn-sm btn-light border" onClick={() => openAssignModal(holidayPackage)}>
                          <IconUsers size={15} className="me-1" /> Assign
                        </button>
                        <button className="btn btn-sm btn-success" onClick={() => openUseModal(holidayPackage)}>
                          <IconCalendarPlus size={15} className="me-1" /> Gunakan PH
                        </button>
                        <button className="btn btn-sm btn-outline-danger" onClick={() => deletePackage(holidayPackage)} disabled={saving}>
                          <IconTrash size={15} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="5" className="text-center py-5">
                      <div className="empty">
                        <div className="empty-icon"><IconPackage size={38} className="text-muted opacity-25" /></div>
                        <p className="empty-title">Belum ada Holiday Package</p>
                        <p className="empty-subtitle text-muted">Klik “Buat Package” untuk set quota PH dulu.</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <PackageModal modal={modal} year={currentYear} saving={saving} onClose={() => setModal(null)} onSubmit={submitPackage} />
      <StaffPickerModal modal={modal} users={users.data || []} saving={saving} onClose={() => setModal(null)} onSubmit={submitAssignUsers} />
      <UseHolidayModal
        modal={modal}
        assignments={modal?.type === "use" ? modal.assignments || [] : []}
        users={users.data || []}
        saving={saving}
        onClose={() => setModal(null)}
        onSubmit={submitUseHoliday}
      />
    </>
  );
};

const PackageModal = ({ modal, year, saving, onClose, onSubmit }) => {
  if (modal?.type !== "package") return null;
  const packageData = modal.package || {};
  const isEdit = modal.mode === "edit";

  return (
    <Offcanvas show onHide={onClose} placement="end" style={{ width: "430px" }}>
      <form onSubmit={onSubmit} className="d-flex flex-column h-100">
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title>{isEdit ? "Edit Holiday Package" : "Buat Holiday Package"}</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="bg-light">
          <div className="mb-3">
            <label className="form-label">Nama Package</label>
            <input className="form-control" name="name" defaultValue={packageData.name || `Cuti Bersama ${year}`} required />
          </div>
          <div className="row g-2 mb-3">
            <div className="col-6">
              <label className="form-label">Start Date</label>
              <input className="form-control" type="date" name="start_date" defaultValue={toDateKey(packageData.start_date) || `${year}-01-01`} required />
            </div>
            <div className="col-6">
              <label className="form-label">End Date</label>
              <input className="form-control" type="date" name="end_date" defaultValue={toDateKey(packageData.end_date) || `${year}-01-10`} required />
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">Quota per Staff</label>
            <input className="form-control" type="number" min="1" name="quota" defaultValue={packageData.quota || 2} required />
            <div className="form-hint">Jumlah maksimal hari PH yang boleh dipakai tiap staff dalam periode package.</div>
          </div>
          <div className="mb-3">
            <label className="form-label">Deskripsi</label>
            <textarea className="form-control" name="description" rows="3" defaultValue={packageData.description || ""} placeholder="Catatan internal package PH" />
          </div>
        </Offcanvas.Body>
        <div className="p-3 border-top bg-white d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-light border" onClick={onClose}>Batal</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Menyimpan..." : isEdit ? "Update Package" : "Simpan Package"}</button>
        </div>
      </form>
    </Offcanvas>
  );
};

const StaffPickerModal = ({ modal, users, saving, onClose, onSubmit }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [search, setSearch] = useState("");
  const assignments = useMemo(() => modal?.assignments || [], [modal?.assignments]);
  const existingIdsKey = useMemo(() => assignments.map((assignment) => assignment.user_id || assignment.user?.id).filter(Boolean).join(","), [assignments]);

  useEffect(() => {
    if (modal?.type === "assign") {
      setSelectedIds(existingIdsKey ? existingIdsKey.split(",") : []);
      setSearch("");
    }
  }, [existingIdsKey, modal?.type]);

  if (modal?.type !== "assign") return null;

  const toggleUser = (userId) => setSelectedIds((items) => items.includes(userId) ? items.filter((id) => id !== userId) : [...items, userId]);
  const normalizedSearch = search.trim().toLowerCase();
  const visibleUsers = users.filter((user) => `${user.name || ""} ${user.email || ""} ${user.department?.name || ""}`.toLowerCase().includes(normalizedSearch));
  const groupedUsers = Object.entries(
    visibleUsers.reduce((result, user) => {
      const departmentName = getDepartmentName(user).toUpperCase();
      return {
        ...result,
        [departmentName]: [...(result[departmentName] || []), user],
      };
    }, {})
  ).sort(([a], [b]) => a.localeCompare(b));

  return (
    <Offcanvas show onHide={onClose} placement="end" style={{ width: "880px" }}>
      <form onSubmit={(event) => onSubmit(event, selectedIds)} className="d-flex flex-column h-100">
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title>Assign Staff PH</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="d-flex flex-column p-0 bg-light">
          <div className="p-3 bg-white border-bottom">
            <div className="fw-bold mb-2">{modal.package.name}</div>
            <div className="input-icon">
              <span className="input-icon-addon"><IconSearch size={16} /></span>
              <input className="form-control" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cari staff..." autoFocus />
            </div>
            <div className="text-muted small mt-2">{selectedIds.length} staff dipilih</div>
          </div>
          <StaffChecklist groups={groupedUsers} selectedIds={selectedIds} onToggle={toggleUser} />
        </Offcanvas.Body>
        <div className="p-3 border-top bg-white d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-light border" onClick={onClose}>Batal</button>
          <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Menyimpan..." : "Simpan Assignment"}</button>
        </div>
      </form>
    </Offcanvas>
  );
};

const UseHolidayModal = ({ modal, assignments, users, saving, onClose, onSubmit }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [dates, setDates] = useState([""]);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (modal?.type !== "use") return;
    setSelectedIds([]);
    setDates([""]);
    setSearch("");
  }, [modal?.package?.id, modal?.type]);

  if (modal?.type !== "use") return null;

  const assignedUsers = assignments.map((assignment) => assignment.user || users.find((user) => user.id === assignment.user_id)).filter(Boolean);
  const normalizedSearch = search.trim().toLowerCase();
  const visibleUsers = assignedUsers.filter((user) => `${user.name || ""} ${user.email || ""} ${user.department?.name || ""}`.toLowerCase().includes(normalizedSearch));
  const validDates = dates.map(toDateKey).filter(Boolean);
  const toggleUser = (userId) => setSelectedIds((items) => items.includes(userId) ? items.filter((id) => id !== userId) : [...items, userId]);

  return (
    <Offcanvas show onHide={onClose} placement="end" style={{ width: "470px" }}>
      <form onSubmit={(event) => onSubmit(event, selectedIds, validDates)} className="d-flex flex-column h-100">
        <Offcanvas.Header closeButton className="border-bottom">
          <Offcanvas.Title>Gunakan PH</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body className="d-flex flex-column p-0 bg-light">
          <div className="p-3 bg-white border-bottom">
            <div className="fw-bold">{modal.package.name}</div>
            <div className="text-muted small mb-3">Akan dibuat sebagai draft schedule agar tetap bisa dicek sebelum publish.</div>
            <label className="form-label">Tanggal PH</label>
            {dates.map((date, index) => (
              <div className="d-flex gap-2 mb-2" key={index}>
                <input
                  className="form-control"
                  type="date"
                  min={toDateKey(modal.package.start_date)}
                  max={toDateKey(modal.package.end_date)}
                  value={date}
                  onChange={(event) => setDates((items) => items.map((item, itemIndex) => itemIndex === index ? event.target.value : item))}
                />
                <button type="button" className="btn btn-light border" onClick={() => setDates((items) => items.filter((_, itemIndex) => itemIndex !== index))} disabled={dates.length === 1}>Hapus</button>
              </div>
            ))}
            <button type="button" className="btn btn-sm btn-light border" onClick={() => setDates((items) => [...items, ""])}>Tambah Tanggal</button>
            <div className="input-icon mt-3">
              <span className="input-icon-addon"><IconSearch size={16} /></span>
              <input className="form-control" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cari staff yang sudah di-assign..." />
            </div>
            <div className="text-muted small mt-2">{selectedIds.length} staff dipilih</div>
          </div>
          <StaffChecklist users={visibleUsers} selectedIds={selectedIds} onToggle={toggleUser} />
        </Offcanvas.Body>
        <div className="p-3 border-top bg-white d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-light border" onClick={onClose}>Batal</button>
          <button type="submit" className="btn btn-success" disabled={saving || !selectedIds.length || !validDates.length}>
            {saving ? "Menyimpan..." : "Simpan Draft PH"}
          </button>
        </div>
      </form>
    </Offcanvas>
  );
};

const StaffChecklist = ({ users = [], groups = null, selectedIds, onToggle }) => (
  <div className="flex-grow-1 overflow-auto p-3">
    {groups?.length ? groups.map(([departmentName, departmentUsers]) => (
      <div className="ph-staff-department" key={departmentName}>
        <div className="ph-staff-department-title">{departmentName}</div>
        <div className="ph-staff-grid">
          {departmentUsers.map((user) => (
            <StaffChecklistCard key={user.id} user={user} isSelected={selectedIds.includes(user.id)} onToggle={onToggle} />
          ))}
        </div>
      </div>
    )) : users.length ? (
      <div className="ph-staff-grid">
        {users.map((user) => (
          <StaffChecklistCard key={user.id} user={user} isSelected={selectedIds.includes(user.id)} onToggle={onToggle} />
        ))}
      </div>
    ) : (
      <div className="empty py-5">
        <p className="empty-title">Tidak ada staff</p>
        <p className="empty-subtitle text-muted">Coba ubah pencarian atau assign staff dulu.</p>
      </div>
    )}
  </div>
);

const StaffChecklistCard = ({ user, isSelected, onToggle }) => {
  const initials = (user.name || "U").split(" ").map((item) => item[0]).join("").slice(0, 2).toUpperCase();

  return (
    <label className={`ph-staff-card ${isSelected ? "is-selected" : ""}`}>
      <input className="form-check-input" type="checkbox" checked={isSelected} onChange={() => onToggle(user.id)} />
      <span className="ph-staff-avatar">{initials}</span>
      <span className="min-w-0">
        <strong>{user.name}</strong>
        <small>{getDepartmentName(user)} {user.email ? `| ${user.email}` : ""}</small>
      </span>
    </label>
  );
};

export default PHHubQuotaTab;
